import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ColorAdvice4, Spacing8 } from 'autogen/design-tokens/tokens';
import type { ConnectedContentTopBannerModel } from 'autogen/swagger/all';
import { Back } from 'autogen/translation-keys/trans-website-shared';
import FullwidthConnectedContent from 'components/connectedContent/fullwidthConnectedContent';
import Card from 'components/generic/card';
import InlineLinkActionButton from 'components/generic/InlineButton/InlineLinkActionButton';
import InlinePageActionButton from 'components/generic/InlineButton/InlinePageActionButton';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import Layout from 'components/Layout';
import Flex from 'components/Layout/components/Flex';
import useGA4Tracking from 'modules/tracking/GA4';
import { breakpoint } from 'variables';

const StyledCard = styled(Card)`
    background-color: ${ColorAdvice4};
`;

const BannerContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing8};
    align-items: flex-start;

    ${breakpoint.up('768px')} {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
`;

/**
 * Important notes about SurveyResultBanner.
 * SurveyResultBanner is actually a full width connected content item, notice that the resultBanner property is of the type "ConnectedContentFullwidthModel".
 * Therefore you will notice a lot of unused properties, that primarily belongs to full width connected content item.
 * @returns
 */
export default function ConnectedContentTopBanner({ model }: { model: ConnectedContentTopBannerModel }) {
    const { t } = useTranslation();
    const GA4Tracking = useGA4Tracking();
    const { title, backLink, topBanner } = model;
    const { dataLayer, link, heading, text, ctaText, isSurveyResult } = topBanner;

    if (!isSurveyResult) {
        return (
            <Layout.Section>
                <FullwidthConnectedContent model={topBanner} />
            </Layout.Section>
        );
    }

    return (
        <Layout.Section
            heading={title}
            headingRenderer={() => (
                <Flex.Column alignItems="start">
                    <InlineLinkActionButton.WithIcon icon={<Icon type="ChevronLeft" size={24} />} action={backLink}>
                        {t(Back)}
                    </InlineLinkActionButton.WithIcon>
                </Flex.Column>
            )}
        >
            <StyledCard data-datalayer={JSON.stringify(dataLayer)}>
                <Card.Content>
                    <BannerContent>
                        <Flex.Column gap={Spacing8} alignItems="start">
                            <Typography.Title size="S">{heading}</Typography.Title>
                            <Typography.Body as="span">{text}</Typography.Body>
                        </Flex.Column>
                        {link?.pageAction && (
                            <InlinePageActionButton
                                fontWeight="semibold"
                                action={link.pageAction}
                                onClick={() =>
                                    GA4Tracking({
                                        eventName: 'open_modal',
                                        category: 'advisory',
                                        eventParams: [
                                            { param_name: 'mini_quiz_name', param_value: title },
                                            { param_name: 'link_text', param_value: ctaText || '' }
                                        ]
                                    })
                                }
                            >
                                {ctaText}
                            </InlinePageActionButton>
                        )}
                    </BannerContent>
                </Card.Content>
            </StyledCard>
        </Layout.Section>
    );
}
